import type { Ref } from "react";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";

/** The single unique-prefix match offered under the manual field (scan-followups 03). */
export interface ScanFallbackSuggestion {
  /** The full code submitted when the suggestion is chosen. */
  code: string;
  /** The entity's display name. */
  name: string;
  /** Container path, or the item's placement path; null when it has none. */
  path: string | null;
  /** First 3 characters of the matched identifier. */
  shortCode: string;
}

interface ScanFallbackProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder: string;
  submitLabel: string;
  /** Accessible name for the field; defaults to the placeholder. */
  inputAriaLabel?: string;
  /** Focus target for the caller (the global preset focuses it on open). */
  inputRef?: Ref<HTMLInputElement>;
  /**
   * The one unique match for the typed prefix. It is its own focusable control;
   * entering the field's text is never rewritten to it.
   */
  suggestion?: ScanFallbackSuggestion | null;
  /** Choose a suggestion: submits its full code through the caller's resolver. */
  onSelectSuggestion?: (code: string) => void;
  /** Escape while typing: the caller hides the field and restores focus. */
  onEscape?: () => void;
  sx?: SxProps<Theme>;
}

/**
 * Typed barcode fallback shared by every preset (scanner-surface 02/05): the
 * lookup field and its submit button. The caller owns what a submit does
 * (container-scoped lookup, provider lookup, or the global resolver), so the
 * flows differ only in copy and handler, not in markup.
 *
 * The global preset uses the optional suggestion slot: while typing a prefix,
 * the single entity whose identifier starts with it is offered below the field
 * and selected with click or Enter (scan-followups 03). A prefix is never
 * resolved on its own — only the suggestion's full code is submitted.
 *
 * @param value current input
 * @param onChange input change handler
 * @param onSubmit submit handler (Enter or the button)
 * @param placeholder field placeholder
 * @param submitLabel button label
 * @param inputAriaLabel accessible name; falls back to the placeholder
 * @param inputRef focus target for the field
 * @param suggestion the unique prefix match, if any
 * @param onSelectSuggestion submit the suggested full code
 * @param onEscape close the field from the keyboard
 */
export function ScanFallback({
  value,
  onChange,
  onSubmit,
  placeholder,
  submitLabel,
  inputAriaLabel,
  inputRef,
  suggestion,
  onSelectSuggestion,
  onEscape,
  sx,
}: ScanFallbackProps) {
  // Enter and the submit button resolve exactly what is typed. The suggestion
  // is a separate focusable control: accepting it submits its full code, so a
  // partial prefix is never resolved literally and a complete code typed by
  // hand is never hijacked by a stricter-prefix match (scan-followups 03).
  const submit = () => onSubmit();

  return (
    <Stack
      spacing={1}
      sx={sx}
      onKeyDown={(e) => {
        // Escape closes the manual field from anywhere inside it, not only from
        // the input (the suggestion and submit are focusable too).
        if (e.key !== "Escape" || !onEscape) return;
        e.preventDefault();
        onEscape();
      }}
    >
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={placeholder}
          value={value}
          inputRef={inputRef}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            submit();
          }}
          slotProps={{ htmlInput: { "aria-label": inputAriaLabel ?? placeholder } }}
        />
        <Button variant="contained" disabled={!value.trim()} onClick={submit}>
          {submitLabel}
        </Button>
      </Stack>
      {suggestion && (
        <ButtonBase
          data-testid="scan-suggestion"
          onClick={() => onSelectSuggestion?.(suggestion.code)}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            gap: 0.25,
            width: "100%",
            p: 1,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            textAlign: "left",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {suggestion.name}
          </Typography>
          {suggestion.path && (
            <Typography variant="caption" color="text.secondary">
              {suggestion.path}
            </Typography>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
          >
            {suggestion.shortCode}
          </Typography>
        </ButtonBase>
      )}
    </Stack>
  );
}
